/* eslint-disable @typescript-eslint/interface-name-prefix */
import React, { useEffect, useState } from 'react';
import ConnectMain from './Connect';
import { QRCodeSVG } from 'qrcode.react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { cn } from '../util';
import { CONNECT_MAP } from '../util/const';
import { ConnectOptions, ConnectResult } from '../TomoContext';
import useIphoneFocusFix from '../hooks/useIphoneFocusFix';
import LoadingRing from './LoadingRing';
import { SupportedProvider, TomoOptions } from '../v2/types/types';

const OKX_QRCODE_TEXT = 'Please scan the QR code with your OKX APP.';

interface IProps {
  opened: boolean;
  onClose: (fn?: () => void) => void;
  title?: string;
  manifestUrl?: string;
  back?: boolean; // show back icon on left top
  close?: boolean; // show close icon on right top
  setConnected: (connect: boolean) => void;
  supportedProviders: SupportedProvider[];
  supportedConnects: CONNECT_MAP[];
  setProviders: (providers: Record<string, any>) => void;
  tomoOptions?: TomoOptions;
  theme?: 'dark' | 'light';
  connectOptions?: ConnectOptions;
  setConnectResult?: (result: ConnectResult) => void;
}

const TomoModal = ({
  title,
  manifestUrl,
  opened,
  close,
  back,
  onClose,
  setConnected,
  supportedProviders,
  supportedConnects,
  setProviders,
  tomoOptions,
  theme,
  connectOptions,
  setConnectResult,
}: IProps) => {
  const [qrCode, setQrCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isShowBack, setIsShowBack] = useState(!!back);
  const [titleText, setTitleText] = useState(title);

  useEffect(() => {
    setIsShowBack(!!qrCode);
    setTitleText(qrCode ? 'OKX Connect' : title);
  }, [qrCode]);

  const handleClikClose = () => {
    setQrCode('');
    onClose();
  };

  const handleBack = () => {
    setQrCode('');
  };

  useEffect(() => {
    const targetElement = document.querySelector('body');
    opened ? disableBodyScroll(targetElement) : enableBodyScroll(targetElement);
  }, [opened]);

  // remove loading when modal is closed
  useEffect(() => {
    if (!opened) setIsLoading(false)
  }, [opened])

  useIphoneFocusFix(opened);

  return (
    <div
      className={cn(
        'tomo-social tm-select-none tm-fixed tm-left-0 tm-top-0 tm-z-40 tm-flex tm-items-end tm-h-screen tm-w-screen tm-bg-tc1/30 tm-font-switzer tm-overflow-auto tm-text-primary dark:tm-text-primary-dark tm-box-border',
        { 'tm-hidden': !opened }
      )}
      onTouchEnd={handleClikClose}
    >
      <div
        id="tomo-modal-body"
        className={
          'tm-relative sm:tm-relative sm:tm-m-auto sm:tm-w-auto tm-bottom-0 tm-left-0 tm-w-full tm-overflow-hidden tm-rounded-t-2xl sm:tm-rounded-b-2xl animate__animated animate__fadeInUp animate__faster sm:tm-animate-none tm-bg-white dark:tm-bg-[#171717]'
        }
        style={{
          filter: 'drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.30))',
          animationDuration: '0.3s',
        }}
        onTouchEnd={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <div className="tm-flex tm-items-center tm-py-4 tm-px-5 tm-justify-between">
          {isShowBack && (
            <BackIcon
              onClick={handleBack}
              className="tm-cursor-pointer tm-text-tc1 dark:tm-text-tc1-dark"
            />
          )}
          <span
            className={cn(
              'tm-flex-1 tm-text-[20px] tm-font-switzerBold tm-text-[#12122A] dark:tm-text-white',
              {
                'tm-text-center': isShowBack,
              }
            )}
          >
            {titleText}
          </span>
          {close && (
            <div
              className="tm-w-[20px] tm-h-[20px] tm-flex tm-items-center tm-justify-center"
              onTouchEnd={handleClikClose}
              onClick={handleClikClose}
            >
              <CloseIcon className="tm-cursor-pointer tm-text-[#12122A] dark:tm-text-white" />
            </div>
          )}
        </div>

        {qrCode && (
          <div className="tm-p-8 tm-flex tm-gap-6 tm-flex-col tm-items-center tm-justify-center">
            <div className="tm-p-1 tm-pb-0 tm-bg-white">
              <QRCodeSVG value={qrCode} />
            </div>
            <span className="tm-px-8 tm-text-center">{OKX_QRCODE_TEXT}</span>
          </div>
        )}
        <ConnectMain
          setConnected={setConnected}
          show={!qrCode}
          setIsLoading={setIsLoading}
          closeModal={handleClikClose}
          chain={supportedProviders[0]}
          supportedConnects={supportedConnects}
          setProviders={setProviders}
          manifestUrl={manifestUrl}
          tomoOptions={tomoOptions}
          theme={theme}
          connectOptions={connectOptions}
          setConnectResult={setConnectResult}
        />
        {isLoading && (
          <div
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            className="tm-absolute tm-z-40 tm-top-0  tm-left-0 tm-right-0 tm-bottom-0 tm-flex tm-items-center tm-justify-center"
          >
            <LoadingRing />
          </div>
        )}
      </div>
    </div>
  );
};

export default TomoModal;

function CloseIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M9.99983 8.82208L14.1247 4.69727L15.3032 5.87577L11.1783 10.0006L15.3032 14.1253L14.1247 15.3038L9.99983 11.1791L5.87505 15.3038L4.69653 14.1253L8.82133 10.0006L4.69653 5.87577L5.87505 4.69727L9.99983 8.82208Z"
        fill="currentColor"
      />
    </svg>
  );
}

function BackIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      {...props}
    >
      <g id="chevron_left_b">
        <path
          id="chevron"
          d="M15.7071 2.79289C15.3166 2.40237 14.6834 2.40237 14.2929 2.79289L5.80762 11.2782C5.41709 11.6687 5.41709 12.3019 5.80762 12.6924L14.2929 21.1777C14.6834 21.5682 15.3166 21.5682 15.7071 21.1777C16.0976 20.7871 16.0976 20.154 15.7071 19.7635L7.92894 11.9853L15.7071 4.20711C16.0976 3.81658 16.0976 3.18342 15.7071 2.79289Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
}
